import { useState, useEffect, useMemo } from "react";
import Box from "@mui/material/Box";
import Typography from "@mui/material/Typography";
import Button from "@mui/material/Button";
import Stack from "@mui/material/Stack";
import IconButton from "@mui/material/IconButton";
import Menu from "@mui/material/Menu";
import MenuItem from "@mui/material/MenuItem";
import Dialog from "@mui/material/Dialog";
import DialogTitle from "@mui/material/DialogTitle";
import DialogContent from "@mui/material/DialogContent";
import DialogActions from "@mui/material/DialogActions";
import TextField from "@mui/material/TextField";
import CircularProgress from "@mui/material/CircularProgress";
import Snackbar from "@mui/material/Snackbar";
import Alert from "@mui/material/Alert";
import AddIcon from "@mui/icons-material/Add";
import EditIcon from "@mui/icons-material/Edit";
import DeleteIcon from "@mui/icons-material/Delete";
import DeleteSweepIcon from "@mui/icons-material/DeleteSweep";
import MoreHorizIcon from "@mui/icons-material/MoreHoriz";
import SendToMobileIcon from "@mui/icons-material/SendToMobile";
import DownloadIcon from "@mui/icons-material/Download";
import { DataGrid } from "@mui/x-data-grid";
import { useTranslation } from "react-i18next";
import {
  getStockCards,
  postStockCard,
  putStockCard,
  deleteStockCard,
  checkStockUsageInSlots,
} from "../../api/stockCardsAPI";
import { createAndDownloadExcel, generateExcelFilename } from "../../utils/excelExportUtils";
import { getNode } from "../../api/nodeAPI";
import { getNodeSlotDefinition } from "../../api/nodeSlotDefinitionAPI";
import { getSlots, postSlots } from "../../api/slotsAPI";

export default function StockGrid() {
  const { t } = useTranslation();

  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(false);

  const [anchorEl, setAnchorEl] = useState(null);
  const [selectedRow, setSelectedRow] = useState(null);
  const open = Boolean(anchorEl);

  const [dialogOpen, setDialogOpen] = useState(false);
  const [isEditMode, setIsEditMode] = useState(false);
  const [formData, setFormData] = useState({
    idstockTable: 0,
    stockName: "",
    stockPrice: 0,
    customerCode: "",
  });

  const [snackbar, setSnackbar] = useState({
    open: false,
    message: "",
    severity: "success",
  });

  const [formErrors, setFormErrors] = useState({
    stockName: "",
    stockPrice: "",
    customerCode: "",
  });

  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [selectedRows, setSelectedRows] = useState([]);
  const [bulkDeleteConfirmDialog, setBulkDeleteConfirmDialog] = useState({
    open: false,
    type: null, // 'selected' or 'all'
  });

  // Otomatlara Ata dialog states
  const [assignDialogOpen, setAssignDialogOpen] = useState(false);
  const [assignStock, setAssignStock] = useState(null);
  const [nodesWithSlotDef, setNodesWithSlotDef] = useState([]);
  const [selectedNodeIds, setSelectedNodeIds] = useState([]);
  const [assignLoading, setAssignLoading] = useState(false);
  const [assignSlotNo, setAssignSlotNo] = useState("");
  const [assignItemCount, setAssignItemCount] = useState("1");

  const handleMenuClick = (event, row) => {
    setAnchorEl(event.currentTarget);
    setSelectedRow(row);
  };

  const handleMenuClose = () => {
    setAnchorEl(null);
    setSelectedRow(null);
  };

  const handleCloseSnackbar = () => {
    setSnackbar({ ...snackbar, open: false });
  };

  const handleEdit = () => {
    setIsEditMode(true);
    setFormData({
      idstockTable: selectedRow.idstockTable,
      stockName: selectedRow.stockName,
      stockPrice: selectedRow.stockPrice, // Kuruş cinsinden tut
      customerCode: selectedRow.customerCode,
    });
    setDialogOpen(true);
    handleMenuClose();
  };

  const handleDelete = () => {
    setDeleteDialogOpen(true);
    setAnchorEl(null);
    // Don't clear selectedRow here, we need it for deletion
  };

  const handleDeleteConfirm = async () => {
    try {
      setLoading(true);
      setDeleteDialogOpen(false);

      // Önce bu ürünün herhangi bir slot'ta kullanılıp kullanılmadığını kontrol et
      const usageCheck = await checkStockUsageInSlots(selectedRow.idstockTable);

      if (usageCheck.isUsed) {
        // Ürün slot'larda kullanılıyor, silme işlemini engelle
        const slotInfo = usageCheck.slots
          .map((slot) => `Slot ${slot.slotNo} (${slot.slotDefinitionTable_name})`)
          .join(", ");

        setSnackbar({
          open: true,
          message: `${t("stocks.stockGrid.deleteError")}: Bu ürün ${usageCheck.count} slot'ta kullanılıyor. Önce bu slotlardan kaldırılmalıdır. (${slotInfo})`,
          severity: "error",
        });

        setSelectedRow(null);
        setLoading(false);
        return;
      }

      // Ürün kullanılmıyorsa silme işlemine devam et
      await deleteStockCard(selectedRow.idstockTable);

      // Refresh the grid data
      const data = await getStockCards();
      const formattedRows = data.map((item) => ({
        id: item.idstockTable,
        idstockTable: item.idstockTable,
        stockName: item.stockName,
        stockPrice: item.stockPrice,
        customerCode: item.customerCode,
      }));
      setRows(formattedRows);

      setSnackbar({
        open: true,
        message: t("stocks.stockGrid.deleteSuccess"),
        severity: "success",
      });

      setSelectedRow(null);
    } catch (error) {
      console.error("Error deleting stock card:", error);
      setSnackbar({
        open: true,
        message: t("stocks.stockGrid.deleteError"),
        severity: "error",
      });
    } finally {
      setLoading(false);
    }
  };

  const handleDeleteCancel = () => {
    setDeleteDialogOpen(false);
    setSelectedRow(null);
  };

  const handleOpenBulkDeleteConfirm = (type) => {
    setBulkDeleteConfirmDialog({ open: true, type });
  };

  const handleCloseBulkDeleteConfirm = () => {
    setBulkDeleteConfirmDialog({ open: false, type: null });
  };

  const handleBulkDeleteConfirm = async () => {
    const type = bulkDeleteConfirmDialog.type;
    handleCloseBulkDeleteConfirm();

    if (type === 'selected') {
      await executeDeleteSelected();
    } else if (type === 'all') {
      await executeDeleteAll();
    }
  };

  const executeDeleteSelected = async () => {
    if (selectedRows.length === 0) return;

    try {
      setLoading(true);
      let deletedCount = 0;
      let skippedCount = 0;

      for (const id of selectedRows) {
        // Her ürün için kullanım kontrolü yap
        const usageCheck = await checkStockUsageInSlots(id);
        if (usageCheck.isUsed) {
          skippedCount++;
          continue;
        }
        await deleteStockCard(id);
        deletedCount++;
      }

      // Seçimi temizle
      setSelectedRows([]);

      // Listeyi yeniden yükle
      const data = await getStockCards();
      const formattedRows = data.map((item) => ({
        id: item.idstockTable,
        idstockTable: item.idstockTable,
        stockName: item.stockName,
        stockPrice: item.stockPrice,
        customerCode: item.customerCode,
      }));
      setRows(formattedRows);

      if (skippedCount > 0) {
        setSnackbar({
          open: true,
          message: `${deletedCount} ürün silindi, ${skippedCount} ürün slot'larda kullanıldığı için silinemedi.`,
          severity: "warning",
        });
      } else {
        setSnackbar({
          open: true,
          message: `${deletedCount} ürün başarıyla silindi.`,
          severity: "success",
        });
      }
    } catch (error) {
      console.error("Error deleting selected stocks:", error);
      setSnackbar({
        open: true,
        message: t('stocks.stockGrid.deleteProductsError', { error: error.message }),
        severity: "error",
      });
    } finally {
      setLoading(false);
    }
  };

  const executeDeleteAll = async () => {
    if (rows.length === 0) return;

    try {
      setLoading(true);
      let deletedCount = 0;
      let skippedCount = 0;

      for (const row of rows) {
        // Her ürün için kullanım kontrolü yap
        const usageCheck = await checkStockUsageInSlots(row.idstockTable);
        if (usageCheck.isUsed) {
          skippedCount++;
          continue;
        }
        await deleteStockCard(row.idstockTable);
        deletedCount++;
      }

      // Seçimi temizle
      setSelectedRows([]);

      // Listeyi yeniden yükle
      const data = await getStockCards();
      const formattedRows = data.map((item) => ({
        id: item.idstockTable,
        idstockTable: item.idstockTable,
        stockName: item.stockName,
        stockPrice: item.stockPrice,
        customerCode: item.customerCode,
      }));
      setRows(formattedRows);

      if (skippedCount > 0) {
        setSnackbar({
          open: true,
          message: `${deletedCount} ürün silindi, ${skippedCount} ürün slot'larda kullanıldığı için silinemedi.`,
          severity: "warning",
        });
      } else {
        setSnackbar({
          open: true,
          message: `${deletedCount} ürün başarıyla silindi.`,
          severity: "success",
        });
      }
    } catch (error) {
      console.error("Error deleting all stocks:", error);
      setSnackbar({
        open: true,
        message: t('stocks.stockGrid.deleteProductsError', { error: error.message }),
        severity: "error",
      });
    } finally {
      setLoading(false);
    }
  };

  // Otomatlara Ata handlers
  const handleOpenAssignDialog = async () => {
    if (!selectedRow) return;

    try {
      setAssignLoading(true);
      setAssignStock(selectedRow);

      // Slot tanımı olan otomatları getir
      const [nodes, nodeSlotDefs, allSlots] = await Promise.all([
        getNode(),
        getNodeSlotDefinition(),
        getSlots(),
      ]);

      // Node'ları slot tanımlarıyla eşleştir
      const nodesWithDefs = nodes
        .map((node) => {
          const slotDef = nodeSlotDefs.find(
            (sd) => Number(sd.nodeId || sd.node_idNode) === Number(node.idNode)
          );
          if (!slotDef) return null;

          // Bu slot tanımına ait mevcut slotları bul
          const existingSlots = allSlots.filter(
            (s) => s.slotDefinitionTable_idslotDefinitionTable === slotDef.slotDefinitionTable_idslotDefinitionTable
          );

          return {
            id: node.idNode,
            idNode: node.idNode,
            name: node.name,
            code: node.code,
            slotDefinitionId: slotDef.slotDefinitionTable_idslotDefinitionTable,
            slotDefinitionName: slotDef.slotDefinitionTable_name,
            existingSlotCount: existingSlots.length,
          };
        })
        .filter(Boolean);

      setNodesWithSlotDef(nodesWithDefs);

      // Seçili ürünün zaten atandığı otomatları önceden seç
      const existingNodeIds = nodesWithDefs
        .filter((node) => {
          const nodeSlotDef = nodeSlotDefs.find(
            (sd) => Number(sd.nodeId || sd.node_idNode) === Number(node.idNode)
          );
          if (!nodeSlotDef) return false;

          // Bu slot tanımında seçili ürün var mı?
          return allSlots.some(
            (s) =>
              s.slotDefinitionTable_idslotDefinitionTable === nodeSlotDef.slotDefinitionTable_idslotDefinitionTable &&
              s.stockTable_idStockTable === selectedRow.idstockTable
          );
        })
        .map((node) => node.idNode);

      setSelectedNodeIds(existingNodeIds);
      setAssignDialogOpen(true);
      handleMenuClose();
    } catch (error) {
      console.error("Error loading nodes:", error);
      setSnackbar({
        open: true,
        message: t("stocks.stockGrid.assignLoadError") || "Otomatlar yüklenirken hata oluştu",
        severity: "error",
      });
    } finally {
      setAssignLoading(false);
    }
  };

  const handleCloseAssignDialog = () => {
    setAssignDialogOpen(false);
    setAssignStock(null);
    setNodesWithSlotDef([]);
    setSelectedNodeIds([]);
    setAssignSlotNo("");
    setAssignItemCount("1");
  };

  const handleAssignToMachines = async () => {
    if (!assignStock || selectedNodeIds.length === 0) return;

    const slotNo = parseInt(assignSlotNo);
    const itemCount = parseInt(assignItemCount) || 1;

    if (isNaN(slotNo) || slotNo < 0) {
      setSnackbar({
        open: true,
        message: t("stocks.stockGrid.invalidSlotNo") || "Geçerli bir slot numarası giriniz",
        severity: "error",
      });
      return;
    }

    try {
      setAssignLoading(true);
      let successCount = 0;
      let errorCount = 0;

      for (const nodeId of selectedNodeIds) {
        const nodeData = nodesWithSlotDef.find((n) => n.idNode === nodeId);
        if (!nodeData) continue;

        try {
          const slotData = {
            slotNo: slotNo,
            itemCount: itemCount,
            priceOverride: 0,
            slotPrice: 0,
            slotDefinitionTable_idslotDefinitionTable: nodeData.slotDefinitionId,
            stockTable_idStockTable: assignStock.idstockTable,
          };

          await postSlots(slotData);
          successCount++;
        } catch (error) {
          console.error(`Error assigning to node ${nodeId}:`, error);
          errorCount++;
        }
      }

      handleCloseAssignDialog();

      if (errorCount > 0) {
        setSnackbar({
          open: true,
          message: t("stocks.stockGrid.assignPartialSuccess", { success: successCount, error: errorCount }) ||
            `${successCount} otomata atandı, ${errorCount} hata oluştu.`,
          severity: "warning",
        });
      } else {
        setSnackbar({
          open: true,
          message: t("stocks.stockGrid.assignSuccess", { count: successCount }) ||
            `${successCount} otomata başarıyla atandı.`,
          severity: "success",
        });
      }
    } catch (error) {
      console.error("Error assigning stock:", error);
      setSnackbar({
        open: true,
        message: t("stocks.stockGrid.assignError") || "Atama sırasında hata oluştu",
        severity: "error",
      });
    } finally {
      setAssignLoading(false);
    }
  };

  // Otomat listesi için kolonlar
  const assignNodeColumns = useMemo(() => [
    {
      field: "code",
      headerName: t("places.dataGrid.code") || "Kod",
      flex: 1,
      headerAlign: "center",
      align: "center",
    },
    {
      field: "slotDefinitionName",
      headerName: t("stocks.nodeSlotGrid.slotDefinitionName") || "Slot Tanımı",
      flex: 1.5,
      headerAlign: "center",
      align: "center",
    },
  ], [t]);

  const handleOpenDialog = () => {
    setIsEditMode(false);
    setDialogOpen(true);
  };

  const handleCloseDialog = () => {
    setDialogOpen(false);
    setIsEditMode(false);
    setFormData({
      idstockTable: 0,
      stockName: "",
      stockPrice: 0,
      customerCode: "",
    });
    setFormErrors({
      stockName: "",
      stockPrice: "",
      customerCode: "",
    });
  };

  const handleFormChange = (field, value) => {
    setFormData((prev) => ({
      ...prev,
      [field]: value,
    }));
  };

  const validateForm = () => {
    const errors = {
      stockName: "",
      stockPrice: "",
      customerCode: "",
    };
    let isValid = true;

    if (!formData.stockName.trim()) {
      errors.stockName = t("validation.required", "Bu alan zorunludur");
      isValid = false;
    }

    if (formData.stockPrice <= 0) {
      errors.stockPrice = t("validation.priceRequired", "Fiyat 0'dan büyük olmalıdır");
      isValid = false;
    }

    if (!formData.customerCode.trim()) {
      errors.customerCode = t("validation.required", "Bu alan zorunludur");
      isValid = false;
    }

    setFormErrors(errors);
    return isValid;
  };

  const handleSubmit = async () => {
    if (!validateForm()) {
      return;
    }

    try {
      setLoading(true);

      if (isEditMode) {
        await putStockCard(
          formData.idstockTable,
          formData.stockName,
          formData.stockPrice,
          formData.customerCode
        );
      } else {
        await postStockCard(
          formData.stockName,
          formData.stockPrice,
          formData.customerCode
        );
      }

      // Refresh the grid data
      const data = await getStockCards();
      const formattedRows = data.map((item) => ({
        id: item.idstockTable,
        idstockTable: item.idstockTable,
        stockName: item.stockName,
        stockPrice: item.stockPrice,
        customerCode: item.customerCode,
      }));
      setRows(formattedRows);

      handleCloseDialog();

      setSnackbar({
        open: true,
        message: isEditMode
          ? t("stocks.stockGrid.updateSuccess")
          : t("stocks.stockGrid.createSuccess"),
        severity: "success",
      });
    } catch (error) {
      console.error(
        isEditMode ? "Error updating stock card:" : "Error creating stock card:",
        error
      );
      setSnackbar({
        open: true,
        message: isEditMode
          ? t("stocks.stockGrid.updateError")
          : t("stocks.stockGrid.createError"),
        severity: "error",
      });
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    const fetchStockCards = async () => {
      try {
        setLoading(true);
        const data = await getStockCards();

        // API'den gelen veriyi DataGrid formatına dönüştür
        const formattedRows = data.map((item) => ({
          id: item.idstockTable,
          idstockTable: item.idstockTable,
          stockName: item.stockName,
          stockPrice: item.stockPrice,
          customerCode: item.customerCode,
        }));

        setRows(formattedRows);
      } catch (error) {
        console.error("Error fetching stock cards:", error);
      } finally {
        setLoading(false);
      }
    };

    fetchStockCards();
  }, []);

  const columns = [
    {
      field: "actions",
      headerName: "",
      width: 70,
      flex: 0.3,
      align: "center",
      headerAlign: "center",
      sortable: false,
      filterable: false,
      renderCell: (params) => {
        return (
          <IconButton
            size="small"
            onClick={(event) => {
              event.stopPropagation();
              handleMenuClick(event, params.row);
            }}
          >
            <MoreHorizIcon />
          </IconButton>
        );
      },
    },
    {
      field: "stockName",
      headerName: t("stocks.stockGrid.stockName"),
      width: 200,
      flex: 1.5,
      headerAlign: "center",
      align: "center",
    },
    {
      field: "stockPrice",
      headerName: t("stocks.stockGrid.stockPrice"),
      width: 150,
      flex: 1,
      headerAlign: "center",
      align: "center",
      type: "number",
      valueFormatter: (value) => {
        if (value == null) return "";
        // Kuruştan liraya çevir (100'e böl)
        return `${(value / 100).toFixed(2)} ₺`;
      },
    },
    {
      field: "customerCode",
      headerName: t("stocks.stockGrid.customerCode"),
      width: 150,
      flex: 1,
      headerAlign: "center",
      align: "center",
    },
  ];

  return (
    <Box sx={{ width: "100%" }}>
      <Stack direction="row" spacing={1} sx={{ mb: 2, ml: { xs: 1, sm: 2 }, mr: { xs: 1, sm: 0 }, alignItems: "center", flexWrap: "wrap", gap: 1 }}>
        <Typography variant="h5" sx={{ fontWeight: "bold" }}>
          {t("stocks.stockGrid.title")}
        </Typography>
        <Box sx={{ flex: 1 }} />
        {rows.length > 0 && (
          <Button
            variant="outlined"
            startIcon={<DownloadIcon />}
            onClick={() => {
              const exportColumns = [
                { field: "stockName", headerName: t("stocks.stockGrid.stockName") },
                { field: "stockPrice", headerName: t("stocks.stockGrid.stockPrice") + " (Kuruş)" },
                { field: "customerCode", headerName: t("stocks.stockGrid.customerCode") },
              ];
              createAndDownloadExcel(
                rows,
                exportColumns,
                "Stok Listesi",
                generateExcelFilename("Stok_Listesi")
              );
            }}
          >
            Excel
          </Button>
        )}
        {rows.length > 0 && (
          <Button
            variant="outlined"
            color="error"
            startIcon={<DeleteSweepIcon />}
            onClick={() => handleOpenBulkDeleteConfirm(selectedRows.length > 0 ? 'selected' : 'all')}
          >
            {selectedRows.length > 0
              ? `${t("common.deleteSelected")} (${selectedRows.length})`
              : t("common.deleteAll")}
          </Button>
        )}
        <Button
          variant="contained"
          color="primary"
          startIcon={<AddIcon />}
          onClick={handleOpenDialog}
        >
          {t("common.add")}
        </Button>
      </Stack>
      <Box sx={{ height: { xs: 450, md: 600 } }}>
        <DataGrid
          rows={rows}
          columns={columns}
          loading={loading}
          checkboxSelection
          rowSelectionModel={selectedRows}
          onRowSelectionModelChange={(newSelection) => setSelectedRows(newSelection)}
          initialState={{
            pagination: {
              paginationModel: { pageSize: 25 },
            },
          }}
          pageSizeOptions={[25, 50, 100]}
          disableRowSelectionOnClick
          sx={{
            "& .MuiDataGrid-columnHeaderTitle": {
              fontWeight: "bold",
            },
          }}
        />
      </Box>
      <Menu
        anchorEl={anchorEl}
        open={open}
        onClose={handleMenuClose}
        anchorOrigin={{
          vertical: "bottom",
          horizontal: "right",
        }}
        transformOrigin={{
          vertical: "top",
          horizontal: "right",
        }}
      >
        <MenuItem onClick={handleEdit}>
          <EditIcon sx={{ mr: 1, fontSize: 20 }} />
          {t("common.edit")}
        </MenuItem>
        <MenuItem onClick={handleOpenAssignDialog}>
          <SendToMobileIcon sx={{ mr: 1, fontSize: 20 }} />
          {t("stocks.stockGrid.assignToMachines")}
        </MenuItem>
        <MenuItem onClick={handleDelete} sx={{ color: "error.main" }}>
          <DeleteIcon sx={{ mr: 1, fontSize: 20 }} />
          {t("common.delete")}
        </MenuItem>
      </Menu>

      <Dialog open={dialogOpen} onClose={handleCloseDialog} maxWidth="sm" fullWidth>
        <DialogTitle>
          {isEditMode ? t("stocks.stockGrid.editStock") : t("stocks.stockGrid.addStock")}
        </DialogTitle>
        <DialogContent>
          <Box sx={{ display: "flex", flexDirection: "column", gap: 2, pt: 2 }}>
            <TextField
              label={t("stocks.stockGrid.stockName")}
              fullWidth
              required
              value={formData.stockName}
              onChange={(e) => handleFormChange("stockName", e.target.value)}
              error={Boolean(formErrors.stockName)}
              helperText={formErrors.stockName}
            />
            <TextField
              label={t("stocks.stockGrid.stockPrice") + " (Kuruş)"}
              type="number"
              fullWidth
              required
              value={formData.stockPrice}
              onChange={(e) => handleFormChange("stockPrice", parseInt(e.target.value) || 0)}
              error={Boolean(formErrors.stockPrice)}
              helperText={formErrors.stockPrice || (formData.stockPrice > 0 ? `Lira: ${(formData.stockPrice / 100).toFixed(2)} ₺` : "")}
              inputProps={{ step: "1", min: "0" }}
            />
            <TextField
              label={t("stocks.stockGrid.customerCode")}
              fullWidth
              required
              value={formData.customerCode}
              onChange={(e) => handleFormChange("customerCode", e.target.value)}
              error={Boolean(formErrors.customerCode)}
              helperText={formErrors.customerCode}
            />
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseDialog} disabled={loading}>
            {t("common.cancel")}
          </Button>
          <Button
            onClick={handleSubmit}
            variant="contained"
            color="primary"
            disabled={loading}
          >
            {loading ? <CircularProgress size={20} /> : t("common.save")}
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog
        open={deleteDialogOpen}
        onClose={handleDeleteCancel}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>{t("stocks.stockGrid.deleteConfirmTitle")}</DialogTitle>
        <DialogContent>
          <Typography>
            {t("stocks.stockGrid.deleteConfirm")}
          </Typography>
          {selectedRow && (
            <Box sx={{ mt: 2, p: 2, bgcolor: "grey.100", borderRadius: 1 }}>
              <Typography variant="body2">
                <strong>{t("stocks.stockGrid.stockName")}:</strong> {selectedRow.stockName}
              </Typography>
              <Typography variant="body2">
                <strong>{t("stocks.stockGrid.stockPrice")}:</strong> {(selectedRow.stockPrice / 100)?.toFixed(2)} ₺
              </Typography>
              <Typography variant="body2">
                <strong>{t("stocks.stockGrid.customerCode")}:</strong> {selectedRow.customerCode}
              </Typography>
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={handleDeleteCancel} disabled={loading}>
            {t("common.cancel")}
          </Button>
          <Button
            onClick={handleDeleteConfirm}
            variant="contained"
            color="error"
            disabled={loading}
          >
            {loading ? <CircularProgress size={20} /> : t("common.delete")}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Bulk Delete Confirmation Dialog */}
      <Dialog
        open={bulkDeleteConfirmDialog.open}
        onClose={handleCloseBulkDeleteConfirm}
        maxWidth="xs"
        fullWidth
      >
        <DialogTitle>
          {t("common.confirmDelete")}
        </DialogTitle>
        <DialogContent>
          <Typography>
            {bulkDeleteConfirmDialog.type === 'selected'
              ? t("common.confirmDeleteSelected", { count: selectedRows.length })
              : t("common.confirmDeleteAll", { count: rows.length })}
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseBulkDeleteConfirm}>
            {t("common.cancel")}
          </Button>
          <Button onClick={handleBulkDeleteConfirm} variant="contained" color="error">
            {t("common.delete")}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Otomatlara Ata Dialog */}
      <Dialog
        open={assignDialogOpen}
        onClose={handleCloseAssignDialog}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle>
          {t("stocks.stockGrid.assignToMachines")} - {assignStock?.stockName}
        </DialogTitle>
        <DialogContent>
          <Box sx={{ display: "flex", flexDirection: "column", gap: 2, pt: 2 }}>
            <Typography variant="body2" color="text.secondary">
              {t("stocks.stockGrid.assignDescription")}
            </Typography>

            <Stack direction="row" spacing={2}>
              <TextField
                label={t("stocks.slotGrid.slotNo")}
                type="number"
                size="small"
                value={assignSlotNo}
                onChange={(e) => setAssignSlotNo(e.target.value)}
                sx={{ width: 150 }}
                required
                inputProps={{ min: 0 }}
              />
              <TextField
                label={t("stocks.slotGrid.itemCount")}
                type="number"
                size="small"
                value={assignItemCount}
                onChange={(e) => setAssignItemCount(e.target.value)}
                sx={{ width: 150 }}
                inputProps={{ min: 1 }}
              />
            </Stack>

            {nodesWithSlotDef.length === 0 ? (
              <Box sx={{ p: 3, textAlign: "center" }}>
                <Typography color="text.secondary">
                  {t("stocks.stockGrid.noMachinesWithSlotDef")}
                </Typography>
              </Box>
            ) : (
              <Box sx={{ height: 400 }}>
                <DataGrid
                  rows={nodesWithSlotDef}
                  columns={assignNodeColumns}
                  loading={assignLoading}
                  checkboxSelection
                  rowSelectionModel={selectedNodeIds}
                  onRowSelectionModelChange={(newSelection) => setSelectedNodeIds(newSelection)}
                  disableRowSelectionOnClick
                  initialState={{
                    pagination: {
                      paginationModel: { pageSize: 10 },
                    },
                  }}
                  pageSizeOptions={[10, 25, 50]}
                  sx={{
                    "& .MuiDataGrid-columnHeaderTitle": {
                      fontWeight: "bold",
                    },
                  }}
                />
              </Box>
            )}

            {selectedNodeIds.length > 0 && (
              <Typography variant="body2" color="primary">
                {t("stocks.stockGrid.selectedMachinesCount", { count: selectedNodeIds.length })}
              </Typography>
            )}
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseAssignDialog} disabled={assignLoading}>
            {t("common.cancel")}
          </Button>
          <Button
            onClick={handleAssignToMachines}
            variant="contained"
            color="primary"
            disabled={assignLoading || selectedNodeIds.length === 0 || !assignSlotNo}
            startIcon={assignLoading ? <CircularProgress size={20} /> : <SendToMobileIcon />}
          >
            {t("stocks.stockGrid.assign")}
          </Button>
        </DialogActions>
      </Dialog>

      <Snackbar
        open={snackbar.open}
        autoHideDuration={6000}
        onClose={handleCloseSnackbar}
        anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
      >
        <Alert
          onClose={handleCloseSnackbar}
          severity={snackbar.severity}
          sx={{ width: "100%" }}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
}
